import { useLazyQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import { useEffect, useMemo, useRef, useState } from 'react'

import useT from '@/i18n/useT'

import {
  Option,
  RemoteSearchableSelect,
  RemoteSearchableSelectProps,
} from './RemoteSearchableSelect'

type RemoteSearchableSelectWithUrlParamsProps<
  T extends Option | Option[] | null,
> = Omit<RemoteSearchableSelectProps<T>, 'value' | 'handleChange'> & {
  urlKey: string
  getOneQuery: string
  label: string
  getOneVariables: (id: string) => any
  getOneMap: (data: any) => { [key: string]: string } | null
}
export const RemoteSearchableSelectWithUrlParams = <
  T extends Option | Option[] | null,
>(
  props: React.PropsWithChildren<RemoteSearchableSelectWithUrlParamsProps<T>>,
) => {
  const {
    state: urlState,
    handle: { handleUpdateSearchParams },
  } = useUrlSearchParams()
  const { getOneMap, getOneQuery, getOneVariables, urlKey, ...rest } = props
  const [idValueMap, setIdValueMap] = useState<{ [key: string]: string }>({})
  const haveFetched = useRef<{ [key in string]: boolean }>({})
  const ids = urlState[urlKey] as string[]
  const { t } = useT()
  const value = useMemo(
    () =>
      ids
        ? ids.map((id: string) => ({
            value: id,
            label: idValueMap[id] || t('remoteSearchableSelect.loading'),
          }))
        : [],
    [JSON.stringify(ids), JSON.stringify(idValueMap), t],
  )

  const [getOne, { loading }] = useLazyQuery(getOneQuery)

  const updateMap = (newValues: { label: string; value: string }[]) => {
    newValues.forEach(({ value, label }) => {
      if (!idValueMap[value]) {
        setIdValueMap(prev => ({ ...prev, [value]: label }))
      }
    })
  }

  useEffect(() => {
    if (ids && !loading) {
      ids.forEach(id => {
        if (!idValueMap[id]) {
          if (!haveFetched.current[id]) {
            getOne({ variables: getOneVariables(id) }).then(({ data }) => {
              const map = getOneMap(data)
              if (map && map.id in idValueMap) return
              setIdValueMap(prev => ({ ...prev, ...map }))
            })
            haveFetched.current[id] = true
          }
        }
      })
    }
  }, [ids, loading, getOne, getOneVariables, getOneMap, idValueMap])

  const handleChange = (
    newValue:
      | { label: string; value: string }[]
      | { label: string; value: string }
      | undefined
      | null,
  ) => {
    if (!newValue) {
      handleUpdateSearchParams({
        [urlKey]: undefined,
      })
      return
    }
    if (!Array.isArray(newValue)) {
      updateMap([newValue])
      handleUpdateSearchParams({
        [urlKey]: newValue.value,
      })
    } else {
      updateMap(newValue)
      handleUpdateSearchParams({
        [urlKey]: newValue.map(({ value }) => value),
      })
    }
  }

  return (
    <RemoteSearchableSelect
      {...rest}
      value={value}
      handleChange={handleChange}
    />
  )
}
